import React, { useState, useEffect, useRef } from "react";

import styles from "./MainPage.module.scss";
import Footer from "../../components/Footer";
import Header from "../../components/Header";

const HELP_ITEMS = [
  {
    question: "What formats are book summaries available in?",
    answer:
      "Book summaries are available in 3 formats: ✍️ Text summaries for quick skimming, 🎧 Audio versions for on-the-go learning, and 🎥 Short explainer videos for visual learners. Get to the heart of a book in just 15 minutes.",
  },
  {
    question: "What languages does Penspace support?",
    answer:
      "Penspace is available in 8 languages: English, Hindi, Spanish, French, Bengali, Arabic, Portuguese & Japanese. Knowledge shouldn't have a language barrier.",
  },
  {
    question: "How does the gamified reading experience work?",
    answer:
      "Set daily reading goals (time or pages), win streaks & XP, and unlock personal reading stats. It's Duolingo-style reading that's fun, fast & fulfilling, making learning addictive and rewarding.",
  },
  {
    question: "What is Live Highlighting with OCR?",
    answer:
      "Point your camera to a book page, extract quotes, and save notes instantly. No typing required - just scan & save your favorite ideas. Perfect for capturing insights from physical books.",
  },
  {
    question: "What is the Knowledge-Feed?",
    answer:
      "Our Discover Page features carousels, micro-posts & reels based on book insights, celebrity book recommendations from Elon Musk, Bill Gates, Naval Ravikant & more. Fresh content daily - Scroll. Learn. Repeat.",
  },
];

export default function MainPage() {
  const [activeHelpIndex, setActiveHelpIndex] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [headerStyle, setHeaderStyle] = useState({});
  const [phoneHover, setPhoneHover] = useState(false);
  const headerRef = useRef(null);
  const mainPageRef = useRef(null);

  const handleNavClick = (e, href) => {
    const hash = href.includes("#") ? "#" + href.split("#")[1] : href;
    if (hash.startsWith("#")) {
      e.preventDefault();
      const targetSection = document.querySelector(hash);
      const scrollContainer = mainPageRef.current;
      if (targetSection && scrollContainer) {
        const headerHeight = headerRef.current?.offsetHeight ?? 0;
        const targetPosition = targetSection.offsetTop - headerHeight - 20;
        scrollContainer.scrollTo({ top: Math.max(0, targetPosition), behavior: "smooth" });
      }
      setMobileMenuOpen(false);
    }
  };

  const scrollToTop = (e) => {
    e.preventDefault();
    mainPageRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = "";
    };
  }, []);

  const toggleHelp = (index) => {
    setActiveHelpIndex(activeHelpIndex === index ? null : index);
  };

  useEffect(() => {
    const scrollContainer = mainPageRef.current;
    if (!scrollContainer) return;
    const handleScroll = () => {
      const scrollY = scrollContainer.scrollTop;
      if (scrollY > 50) {
        setHeaderStyle({
          background: "rgba(255, 255, 255, 0.98)",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
        });
      } else {
        setHeaderStyle({
          background: "rgba(255, 255, 255, 0.95)",
          boxShadow: "none",
        });
      }
    };
    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className={styles.mainPage} ref={mainPageRef}>
      <Header
        headerRef={headerRef}
        headerStyle={headerStyle}
        onLogoClick={scrollToTop}
        onNavClick={handleNavClick}
        mobileMenuOpen={mobileMenuOpen}
        onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
      />

      <section className={styles.hero}>
        <div className={styles.container}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>
              Learn from books in just 15 minutes
            </h1>
            <p className={styles.heroDescription}>
              Penspace transforms how you consume knowledge with book summaries
              in 3 formats, gamified reading, and a discovery feed that makes
              learning feel like social media.
            </p>
            <div className={styles.heroButtons}>
              <a
                href="https://play.google.com/store/apps"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.btn}
                aria-label="Get it on Google Play"
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                  alt="Get it on Google Play"
                  className={styles.storeBadge}
                />
              </a>
              <a
                href="https://apps.apple.com/app"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.btn}
                aria-label="Download on App Store"
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
                  alt="Download on App Store"
                  className={styles.storeBadge}
                />
              </a>
            </div>
          </div>
          <div className={styles.heroVisual}>
            <div className={styles.phoneMockup}>
              <div className={styles.phoneScreen}>
                <div className={styles.appPreview}>
                  <div className={styles.previewContent}>
                    <div className={styles.previewHeader}>
                      <div className={styles.previewAvatar} />
                      <div className={styles.previewInfo}>
                        <div className={styles.previewName} />
                        <div className={styles.previewStatus} />
                      </div>
                    </div>
                    <div className={styles.previewBody}>
                      <div className={styles.previewCard} />
                      <div className={styles.previewCard} />
                      <div className={styles.previewCard} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className={styles.features}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>How Penspace works</h2>
          <div className={styles.featuresGrid}>
            <div className={styles.featureItem}>
              <div className={styles.featureNumber}>1</div>
              <h3 className={styles.featureTitle}>Choose your format</h3>
              <p className={styles.featureDescription}>
                Get book summaries in 3 formats: ✍️ Text for quick skimming,
                🎧 Audio for on-the-go learning, 🎥 Videos for visual learners.
              </p>
            </div>
            <div className={styles.featureItem}>
              <div className={styles.featureNumber}>2</div>
              <h3 className={styles.featureTitle}>Learn & level up</h3>
              <p className={styles.featureDescription}>
                Set daily reading goals, win streaks & XP, unlock reading
                stats. Duolingo-style learning that's fun, fast & fulfilling.
              </p>
            </div>
            <div className={styles.featureItem}>
              <div className={styles.featureNumber}>3</div>
              <h3 className={styles.featureTitle}>Discover & connect</h3>
              <p className={styles.featureDescription}>
                Explore your knowledge feed with book insights, celebrity
                recommendations, and content that feels like Instagram but makes
                your brain smarter.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="key-features" className={styles.keyFeatures}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>
            Why millions choose Penspace
          </h2>
          <div className={styles.keyFeaturesGrid}>
            <div className={styles.keyFeature}>
              <div className={styles.featureIcon}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V4C20 2.9 19.1 2 18 2ZM18 20H6V4H18V20Z"
                    fill="#1a73e8"
                  />
                  <path d="M8 6H16V8H8V6Z" fill="#1a73e8" />
                  <path d="M8 10H16V12H8V10Z" fill="#1a73e8" />
                  <path d="M8 14H13V16H8V14Z" fill="#1a73e8" />
                </svg>
              </div>
              <h3>Book Summaries in 3 Formats</h3>
              <p>
                Text summaries for quick skimming
                <br />
                Audio versions for on-the-go learning
                <br />
                Short explainer videos for visual learners
                <br />
                <strong>Get to the heart of a book in just 15 minutes.</strong>
              </p>
            </div>
            <div className={styles.keyFeature}>
              <div className={styles.featureIcon}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
                    fill="#34a853"
                  />
                  <path
                    d="M16.24 7.76C15.07 6.59 13.54 6 12 6V12L16.24 16.24C17.41 15.07 18 13.54 18 12C18 10.46 17.41 8.93 16.24 7.76Z"
                    fill="#34a853"
                  />
                </svg>
              </div>
              <h3>Available in 8 Languages</h3>
              <p>
                English, Hindi, Spanish, French, Bengali, Arabic, Portuguese &
                Japanese
                <br />
                <strong>Knowledge shouldn't have a language barrier.</strong>
              </p>
            </div>
            <div className={styles.keyFeature}>
              <div className={styles.featureIcon}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z"
                    fill="#fbbc04"
                  />
                </svg>
              </div>
              <h3>Celebrity Book Recommendations</h3>
              <p>
                Books loved by Elon Musk, Bill Gates, Naval Ravikant & more
                <br />
                <strong>Read what the world's top minds are reading.</strong>
              </p>
            </div>
            <div className={styles.keyFeature}>
              <div className={styles.featureIcon}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 12C15 10.34 13.66 9 12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12Z"
                    fill="#ea4335"
                  />
                  <path
                    d="M19.43 12.98C19.47 12.66 19.5 12.33 19.5 12C19.5 11.67 19.47 11.34 19.43 11.02L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.66 5.27C19.54 5.05 19.27 4.97 19.05 5.05L16.56 6.05C16.04 5.65 15.48 5.32 14.87 5.07L14.49 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.51 2.42L9.13 5.07C8.52 5.32 7.96 5.66 7.44 6.05L4.95 5.05C4.72 4.96 4.46 5.05 4.34 5.27L2.34 8.73C2.21 8.95 2.27 9.22 2.46 9.37L4.57 11.02C4.53 11.34 4.5 11.67 4.5 12C4.5 12.33 4.53 12.65 4.57 12.97L2.46 14.63C2.27 14.78 2.21 15.05 2.34 15.27L4.34 18.73C4.46 18.95 4.73 19.03 4.95 18.95L7.44 17.95C7.96 18.35 8.52 18.68 9.13 18.93L9.51 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.49 21.58L14.87 18.93C15.48 18.68 16.04 18.34 16.56 17.95L19.05 18.95C19.28 19.04 19.54 18.95 19.66 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.98Z"
                    fill="#ea4335"
                  />
                </svg>
              </div>
              <h3>Gamified Reading Experience</h3>
              <p>
                Set daily reading goals (time or pages)
                <br />
                Win streaks & XP
                <br />
                Unlock personal reading stats
                <br />
                <strong>
                  Duolingo-style reading — fun, fast & fulfilling.
                </strong>
              </p>
            </div>
            <div className={styles.keyFeature}>
              <div className={styles.featureIcon}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V18C3 19.1 3.9 20 5 20H11V18H5V3H13V9H21ZM22.5 18.5L21.5 19.5L19 17L20 16L22.5 18.5ZM13 19.96V22H15.04L21.17 15.88L19.13 13.83L13 19.96Z"
                    fill="#9aa0a6"
                  />
                </svg>
              </div>
              <h3>Live Highlighting with OCR</h3>
              <p>
                Point your camera to a book page, extract quotes, and save
                notes
                <br />
                <strong>No typing. Just scan & save your favorite ideas.</strong>
              </p>
            </div>
            <div className={styles.keyFeature}>
              <div className={styles.featureIcon}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M17 1.01L7 1C5.9 1 5 1.9 5 3V21C5 22.1 5.9 23 7 23H17C18.1 23 19 22.1 19 21V3C19 1.9 18.1 1.01 17 1.01ZM17 19H7V5H17V19Z"
                    fill="#1a73e8"
                  />
                  <path d="M16 6H8V8H16V6Z" fill="#1a73e8" />
                  <path d="M16 9H8V11H16V9Z" fill="#1a73e8" />
                  <path d="M16 12H8V14H16V12Z" fill="#1a73e8" />
                  <path d="M13 15H8V17H13V15Z" fill="#1a73e8" />
                </svg>
              </div>
              <h3>Knowledge-Feed (Discover Page)</h3>
              <p>
                Carousels, micro-posts & reels based on book insights
                <br />
                Fresh content daily
                <br />
                Scroll. Learn. Repeat.
                <br />
                <strong>
                  Feels like Instagram, but your brain gets smarter.
                </strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className={styles.statsSection}>
        <div className={styles.container}>
          <div className={styles.statsGrid}>
            <div className={styles.statItem}>
              <div className={styles.statNumber}>5M+</div>
              <div className={styles.statLabel}>Active Readers</div>
            </div>
            <div className={styles.statItem}>
              <div className={styles.statNumber}>50K+</div>
              <div className={styles.statLabel}>Book Summaries</div>
            </div>
            <div className={styles.statItem}>
              <div className={styles.statNumber}>8</div>
              <div className={styles.statLabel}>Languages</div>
            </div>
            <div className={styles.statItem}>
              <div className={styles.statNumber}>15min</div>
              <div className={styles.statLabel}>Average Read Time</div>
            </div>
          </div>
        </div>
      </section>

      {/* App Preview Section */}
      <section className={styles.appPreviewSection}>
        <div className={styles.container}>
          <div className={styles.appPreviewContent}>
            <div className={styles.appPreviewText}>
              <h2 className={styles.sectionTitle}>
                Experience learning reimagined
              </h2>
              <div className={styles.previewFeatures}>
                <div className={styles.previewFeature}>
                  <div className={styles.previewIcon}>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z"
                        fill="#34a853"
                      />
                    </svg>
                  </div>
                  <span>Personalized learning paths</span>
                </div>
                <div className={styles.previewFeature}>
                  <div className={styles.previewIcon}>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z"
                        fill="#34a853"
                      />
                    </svg>
                  </div>
                  <span>Offline reading capability</span>
                </div>
                <div className={styles.previewFeature}>
                  <div className={styles.previewIcon}>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z"
                        fill="#34a853"
                      />
                    </svg>
                  </div>
                  <span>Smart progress tracking</span>
                </div>
                <div className={styles.previewFeature}>
                  <div className={styles.previewIcon}>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z"
                        fill="#34a853"
                      />
                    </svg>
                  </div>
                  <span>Cross-device synchronization</span>
                </div>
              </div>
            </div>
            <div
              className={styles.appPreviewVisual}
              onMouseEnter={() => setPhoneHover(true)}
              onMouseLeave={() => setPhoneHover(false)}
            >
              <div className={styles.phoneShowcase}>
                <div
                  className={styles.phoneFrame}
                  style={{
                    transform: phoneHover
                      ? "scale(1.05) rotateY(-5deg)"
                      : "scale(1) rotateY(0deg)",
                  }}
                >
                  <div className={styles.phoneScreenContent}>
                    <div className={styles.appInterface}>
                      <div className={styles.appHeader}>
                        <div className={styles.appUser}>
                          <div className={styles.userAvatar} />
                          <div className={styles.userInfo}>
                            <div className={styles.userName}>Good morning!</div>
                            <div className={styles.userStreak}>7 day streak</div>
                          </div>
                        </div>
                        <div className={styles.appNotification}>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.89 22 12 22ZM18 16V11C18 7.93 16.36 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.63 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z"
                              fill="#5f6368"
                            />
                          </svg>
                        </div>
                      </div>
                      <div className={styles.dailyGoal}>
                        <div className={styles.goalText}>Today's Goal</div>
                        <div className={styles.goalProgress}>
                          <div className={styles.progressBar}>
                            <div className={styles.progressFill} />
                          </div>
                          <div className={styles.goalStats}>15/20 min</div>
                        </div>
                      </div>
                      <div className={styles.bookCards}>
                        <div
                          className={`${styles.bookCard} ${styles.featured}`}
                        >
                          <div className={styles.bookCover} />
                          <div className={styles.bookInfo}>
                            <div className={styles.bookTitle}>
                              Atomic Habits
                            </div>
                            <div className={styles.bookAuthor}>
                              James Clear
                            </div>
                            <div className={styles.bookProgress}>
                              <span className={styles.progressText}>
                                12 min left
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className={styles.bookCard}>
                          <div
                            className={`${styles.bookCover} ${styles.small}`}
                          />
                          <div className={styles.bookInfo}>
                            <div className={styles.bookTitle}>
                              Think Fast
                            </div>
                            <div className={styles.bookAuthor}>
                              Dan Kahneman
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Help Section */}
      <section id="help" className={styles.help}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Help center</h2>
          <div className={styles.helpItems}>
            {HELP_ITEMS.map((item, index) => (
              <div
                key={index}
                className={`${styles.helpItem} ${activeHelpIndex === index ? styles.active : ""}`}
              >
                <button
                  className={styles.helpToggle}
                  onClick={() => toggleHelp(index)}
                >
                  <span>{item.question}</span>
                  <span className={styles.helpIcon}>+</span>
                </button>
                <div className={styles.helpContent}>
                  <p>{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section id="contact" className={styles.contactSection}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Contact Us</h2>
          <div className={styles.contactContent}>
            <p className={styles.contactIntro}>
              If you have any questions about this Privacy Policy or our data
              practices, please contact us:
            </p>
            <div className={styles.contactInfo}>
              <div className={styles.contactItem}>
                <h3>Support Email:</h3>
                <a
                  href="mailto:support@penspace.in"
                  className={styles.contactLink}
                >
                  support@penspace.in
                </a>
              </div>
              <div className={styles.contactItem}>
                <h3>Privacy Email:</h3>
                <a
                  href="mailto:privacy@penspace.in"
                  className={styles.contactLink}
                >
                  privacy@penspace.in
                </a>
              </div>
              <div className={styles.contactItem}>
                <h3>Address:</h3>
                <div className={styles.contactAddress}>
                  <p>Penspace Privacy Team</p>
                  <p>At Post Degloor, Degloor</p>
                  <p>Nanded – 431717</p>
                  <p>Maharashtra, India</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer showDeleteAccount />
    </div>
  );
}
